import { createContext, ChangeEvent, DragEvent, MouseEvent, ReactNode, useEffect, useCallback, useState, useRef, useMemo } from 'react';
import { useAppDispatch } from 'redux/store';
import { Button, Form, Spinner } from 'react-bootstrap';

// ReactFlow
import ReactFlow, {
  Controls,
  Background,
  applyNodeChanges,
  applyEdgeChanges,
  addEdge,
  useReactFlow,
  type Connection,
  type EdgeChange,
  type Node,
  type NodeChange,
  type OnSelectionChangeParams,
} from 'reactflow';
import '../../NodeDefinitionEditor.scss';

import { useTranslation } from 'react-i18next';
import { v4 as uuidv4 } from 'uuid';

import api from 'services/api';
import EditStringWithHelp from 'components/edit/EditStringWithHelp';
import SearchBox, { type SearchBoxProps } from 'components/common/SearchBox';
import { Splitter } from 'components/Splitter/Splitter';
import StandardNode from './node_types/StandardNode';
import { flowEdgeTypes } from './edge_types/RoutedEdge';
import { FlowEdgePropertyPanel } from './FlowEdgePropertyPanel';

import { coerceEdgeTypeForCanvas } from '../../edit/flowEdgeHelpers';
import { useFlowEdgeSelection } from '../../edit/useFlowEdgeSelection';
import { panelModeOnSelection, panelTarget, type FlowPanelMode, type FlowPanelTarget } from '../../edit/flowSelection';
import type {
  FlowChildNode,
  FlowEdge,
  FlowHandleStatusAction,
  FlowLibraryNode,
  FlowNodeRaiseEvent,
  FlowNodeWaitEvent,
  FlowParameterOverride,
  FlowSelectedNode,
  FlowSelectedNodeRef,
  FlowState,
} from '../../edit/flowGraph';
import type { Handle } from 'api/node_definitions/endpoints/types';
import type { Parameter } from 'api/node_definitions/endpoints/types';
import { getNodeDefinitionGraph } from 'api/node_definitions/endpoints/getNodeDefinitionGraph';
import { getPlacedNodeDefinition } from 'api/node_definitions/endpoints/getPlacedNodeDefinition';
import type { FlowDefinitionNode } from 'api/node_definitions/endpoints/types';
import { toError } from 'helpers/toError';
import { notify } from 'messages';
import { acceptDegraded } from 'validation';

import { fetchCustomerNodeDefinitions } from '../../../../../../../redux/slices/customer_node_definitionsSlice';
import { EditEventPathReference } from 'edit/reference/EditEventPathReference';
import { EditLockReference } from 'edit/reference/EditLockReference';
import { EditSchedulingWindowReference } from 'edit/reference/EditSchedulingWindowReference';
import { formatSchedulingWindowPath, parseSchedulingWindowPath } from 'edit/reference/schedulingWindowPath';

import Queues from '../ui/Queues';
import ReadOnlyProperties from '../ui/ReadOnlyProperties';
import ScheduleStatus from '../ui/ScheduleStatus';
import ScheduleScheduleStatus from '../ui/ScheduleSchedulingStatus';
import ScheduleStartTime from '../ui/ScheduleStartTime';
import SubflowGroupNode from './node_types/SubflowGroupNode';

export const SubflowNavigationContext = createContext<{
  navigateIntoSubflow: (nodeId: number, name: string) => void;
  isInsideSubflow: boolean;
  onExpandNode: ((nodeId: string) => void) | null;
} | null>(null);

const nodeTypes = {
  default: StandardNode,
  conditions: StandardNode,
  shell: StandardNode,
  bash: StandardNode, // backward compatibility
  flow: StandardNode,
  sap: StandardNode,
  sapibp: StandardNode,
  sapbw: StandardNode,
  rest: StandardNode,
  tableRead: StandardNode,
  sendMail: StandardNode,
  sql: StandardNode,
  sqlplus: StandardNode,
  workflowActionCreate: StandardNode,
  workflowCreate: StandardNode,
  subflowGroup: SubflowGroupNode,
  norscom_python: StandardNode,
};

interface BreadcrumbItem {
  readonly name: string;
}

const SubflowBreadcrumbs = ({ items, onNavigate }: { items: readonly BreadcrumbItem[]; onNavigate: (index: number) => void }) => (
  <div className="subflow-breadcrumbs">
    {items.map((item, i) => (
      <span key={i}>
        {i > 0 && <span className="breadcrumb-separator">›</span>}
        <span className={`breadcrumb-item ${i === items.length - 1 ? 'active' : ''}`} onClick={() => (i < items.length - 1 ? onNavigate(i) : null)}>
          {item.name}
        </span>
      </span>
    ))}
  </div>
);

// Pixel width limits for the two resizable side panes (the canvas always takes
// the remaining width). Fixed pixels — not percentages — so resizing or
// collapsing one side pane only changes the canvas, never the other pane, and
// the panes keep their width when the window resizes.
const FLOW_PANE_LIMITS: Record<'library' | 'panel', { min: number; max: number; default: number }> = {
  library: { min: 170, max: 520, default: 230 },
  panel: { min: 280, max: 640, default: 340 },
};

// Header bar shown at the top of a side pane: title + a chevron that collapses
// the pane to a rail, and an optional close (×) that dismisses the pane's
// content entirely (e.g. deselect the node). `side` decides which way the
// collapse chevron points.
const FlowPaneHeader = ({ title, side, onCollapse, onClose }: { title: string; side: 'left' | 'right'; onCollapse: () => void; onClose?: () => void }) => (
  <div className="flow-pane-header">
    <span className="flow-pane-title text-truncate" title={title}>
      {title}
    </span>
    <span className="flow-pane-header-actions">
      <button type="button" className="flow-pane-toggle" onClick={onCollapse} title="Collapse" aria-label={`Collapse ${title}`}>
        {side === 'left' ? '‹' : '›'}
      </button>
      {onClose && (
        <button type="button" className="flow-pane-toggle" onClick={onClose} title="Close" aria-label={`Close ${title}`}>
          ×
        </button>
      )}
    </span>
  </div>
);

// Thin rail a collapsed pane shrinks to: a chevron to expand + a vertical
// label. Clicking anywhere on the rail expands it.
const FlowPaneRail = ({ title, side, onExpand }: { title: string; side: 'left' | 'right'; onExpand: () => void }) => (
  <div
    className={`flow-pane-rail flow-pane-rail-${side}`}
    onClick={onExpand}
    role="button"
    tabIndex={0}
    title={`Expand ${title}`}
    aria-label={`Expand ${title}`}
  >
    <span className="flow-pane-toggle" aria-hidden>
      {side === 'left' ? '›' : '‹'}
    </span>
    <span className="flow-pane-rail-label">{title}</span>
  </div>
);

interface RenderLibraryNodesProps {
  state: FlowState;
  onDragStart: (event: DragEvent<HTMLDivElement>, node: FlowLibraryNode) => void;
  onSearch: SearchBoxProps['onChange'];
  onSearchSubmit: () => void;
  searchValue: string;
  onLoadMore: () => void;
  hasMore: boolean;
  isLoading: boolean;
  nodes?: readonly FlowLibraryNode[];
}

const RenderLibraryNodes = ({
  state,
  onDragStart,
  onSearch,
  onSearchSubmit,
  searchValue,
  onLoadMore,
  hasMore,
  isLoading,
  nodes = [],
}: RenderLibraryNodesProps) => {
  const { t } = useTranslation();

  const sentinelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (entry.isIntersecting && hasMore && !isLoading) {
          onLoadMore();
        }
      },
      {
        root: null,
        rootMargin: '100px',
        threshold: 0.1,
      }
    );

    observer.observe(sentinel);

    return () => {
      observer.disconnect();
    };
  }, [hasMore, isLoading, onLoadMore]);

  const handleSearchKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      e.stopPropagation();
      onSearchSubmit();
    }
  };

  return (
    <>
      <div onKeyDown={handleSearchKeyDown}>
        <SearchBox placeholder={t('search') + '...'} size="sm" onChange={onSearch} value={searchValue} className="mb-4" style={{ width: '100%' }} />
      </div>
      {nodes.map((node, index) => {
        const { name, type, zone_name } = node;
        return (
          <div
            onDragStart={(event) => onDragStart(event, node)}
            data-type={type}
            data-name={name}
            data-zone={zone_name}
            key={`${node.node_definition_id}-${index}`}
            className="library-node"
            draggable
            // The name truncates in a narrow pane, so the hover title carries the
            // pair that identifies the definition.
            title={zone_name ? `${name} (${zone_name})` : name}
          >
            <span className="text">{name}</span>
            {/* Zone first: it is the discriminator between same-named definitions, and the
                ellipsis on this line eats the tail. Colliding definitions share a type. */}
            <span className="type">{zone_name ? `${zone_name} · ${type}` : type}</span>
          </div>
        );
      })}
      <div ref={sentinelRef} style={{ height: '20px', width: '100%' }}>
        {isLoading && (
          <div className="d-flex justify-content-center py-2">
            <Spinner animation="border" size="sm" />
          </div>
        )}
      </div>
    </>
  );
};

const HANDLE_STATUS_OPTIONS = [
  { value: 'nothing', label: 'Nothing' },
  { value: 'raise_operator_message', label: 'Raise operator message' },
  { value: 'fail_flow', label: 'Fail flow' },
  { value: 'complete_flow', label: 'Complete flow' },
] as const;

const normalizeLockTags = (locks: unknown): string[] =>
  Array.isArray(locks) ? locks.map((lock) => (typeof lock === 'string' ? lock.trim() : '')).filter(Boolean) : [];

export const formatLockTag = (tag: string) => {
  const [zoneName, ...lockNameParts] = tag.split('|');
  const lockName = lockNameParts.join('|');
  return lockName ? `${lockName} (${zoneName})` : tag;
};

const lockTagsAreEqual = (a: string[], b: string[]) => a.length === b.length && a.every((tag, index) => tag === b[index]);

const updateParameterOverride = (parameters: readonly FlowParameterOverride[], name: string | null, updates: Partial<FlowParameterOverride>) => {
  const nextParams = [...parameters];
  const index = nextParams.findIndex((parameter) => parameter.name === name);
  const nextParameter: FlowParameterOverride = index >= 0 ? { ...nextParams[index] } : { name };

  Object.entries(updates).forEach(([key, value]) => {
    if (value === undefined || value === null || ((key === 'value' || key === 'parent_parameter_out') && value === '')) {
      delete (nextParameter as Record<string, unknown>)[key];
    } else {
      (nextParameter as Record<string, unknown>)[key] = value;
    }
  });

  if (index >= 0) {
    nextParams[index] = nextParameter;
  } else {
    nextParams.push(nextParameter);
  }

  return nextParams.filter(
    (parameter) =>
      (Object.prototype.hasOwnProperty.call(parameter, 'value') && parameter.value !== undefined && parameter.value !== null && parameter.value !== '') ||
      Object.prototype.hasOwnProperty.call(parameter, 'runtime') ||
      (Object.prototype.hasOwnProperty.call(parameter, 'parent_parameter_out') &&
        parameter.parent_parameter_out !== undefined &&
        parameter.parent_parameter_out !== null &&
        parameter.parent_parameter_out !== '')
  );
};

const NODE_ID_REGEX = /^[a-zA-Z0-9_]+$/;

// A definition parameter merged with its placement override, as the side panel lists it.
type EditorParameter = Parameter & {
  value?: string;
  parent_parameter_out?: string;
  definitionRuntime: boolean | undefined;
  runtimeOverrideSet: boolean;
};

// react-bootstrap's Form.Control element type (not exported by the library).
type FormControlElement = HTMLInputElement | HTMLTextAreaElement;

interface RenderMiniEditorProps {
  state: FlowState;
  // The node the panel shows: its placed definition's record, with `id` the canvas node id.
  selectedNode: FlowSelectedNode;
  onChange: (event: ChangeEvent<FormControlElement>, name: string | null, nodeId: string) => void;
  onRuntimeChange: (name: string | null, nodeId: string, runtime: boolean | undefined) => void;
  onParentParameterOutChange: (event: ChangeEvent<FormControlElement>, name: string | null, nodeId: string) => void;
  onQueueChange: (event: ChangeEvent<HTMLSelectElement>, nodeId: string) => void;
  onWindowChange: (value: string | null, nodeId: string) => void;
  onWindowStatusChange: (event: ChangeEvent<HTMLSelectElement>, nodeId: string) => void;
  onPreconditionExpressionChange: (event: ChangeEvent<HTMLInputElement>, nodeId: string) => void;
  onScheduleSchedulingWindowChange: (value: string | null, nodeId: string) => void;
  onScheduleSchedulingWindowStatusChange: (event: ChangeEvent<HTMLSelectElement>, nodeId: string) => void;
  onScheduleStartTimeChange: (event: ChangeEvent<HTMLInputElement>, nodeId: string) => void;
  onSubmitDisabledChange: (event: ChangeEvent<HTMLInputElement>, nodeId: string) => void;
  onSubmitAndForgetChange?: (event: ChangeEvent<HTMLInputElement>, nodeId: string) => void;
  onHandleStatusActionChange?: (handleId: string, value: string, nodeId: string) => void;
  onWaitEventAdd?: (nodeId: string) => void;
  onWaitEventRemove: (nodeId: string, eventIndex: number) => void;
  onWaitEventChange: <K extends keyof FlowNodeWaitEvent>(nodeId: string, eventIndex: number, field: K, value: FlowNodeWaitEvent[K]) => void;
  onRaiseEventAdd?: (nodeId: string) => void;
  onRaiseEventRemove: (nodeId: string, eventIndex: number) => void;
  onRaiseEventChange: <K extends keyof FlowNodeRaiseEvent>(nodeId: string, eventIndex: number, field: K, value: FlowNodeRaiseEvent[K]) => void;
  onLockAdd?: (nodeId: string) => void;
  onLockRemove: (nodeId: string, lockIndex: number) => void;
  onLockChange: (nodeId: string, lockIndex: number, value: string | null) => void;
  onNodeIdChange: (oldId: string, newId: string) => void;
  onLabelChange: (event: ChangeEvent<FormControlElement>, nodeId: string) => void;
  onDescriptionChange: (event: ChangeEvent<FormControlElement>, nodeId: string) => void;
}

const RenderMiniEditor = ({
  state,
  selectedNode,
  onChange,
  onRuntimeChange,
  onParentParameterOutChange,
  onQueueChange,
  onWindowChange,
  onWindowStatusChange,
  onPreconditionExpressionChange,
  onScheduleSchedulingWindowChange,
  onScheduleSchedulingWindowStatusChange,
  onScheduleStartTimeChange,
  onSubmitDisabledChange,
  onSubmitAndForgetChange,
  onHandleStatusActionChange,
  onWaitEventAdd,
  onWaitEventRemove,
  onWaitEventChange,
  onRaiseEventAdd,
  onRaiseEventRemove,
  onRaiseEventChange,
  onLockAdd,
  onLockRemove,
  onLockChange,
  onNodeIdChange,
  onLabelChange,
  onDescriptionChange,
}: RenderMiniEditorProps) => {
  const { t } = useTranslation();
  const { child_nodes } = state;
  const { parameters = [], default_queue_path, id } = selectedNode;
  const [editingNodeId, setEditingNodeId] = useState(id || '');
  const [nodeIdError, setNodeIdError] = useState('');

  useEffect(() => {
    setEditingNodeId(id || '');
    setNodeIdError('');
  }, [id]);

  const handleNodeIdBlur = () => {
    if (editingNodeId === id) return;
    if (!editingNodeId.trim()) {
      setNodeIdError('Node ID cannot be empty');
      return;
    }
    if (!NODE_ID_REGEX.test(editingNodeId)) {
      setNodeIdError('Only letters, numbers, and underscores allowed');
      return;
    }
    const isDuplicate = child_nodes.some((node) => node.id === editingNodeId && node.id !== id);
    if (isDuplicate) {
      setNodeIdError('Node ID already exists');
      return;
    }
    setNodeIdError('');
    onNodeIdChange(id, editingNodeId);
  };
  const currentChildNode = child_nodes.find((node) => node.id === id);
  const isSubmitAndForgetPlacement = currentChildNode?.submit_and_forget === true;
  const handles = selectedNode.handles ?? [];
  const selectedScheduleWindow = currentChildNode?.schedule_scheduling_window;
  const selectedScheduleWindowStatus = currentChildNode?.schedule_scheduling_window_status;
  const selectedScheduleStartTime = currentChildNode?.schedule_start_time ?? null;
  const selectedQueue = currentChildNode?.queue_path;
  const selectedWindow = currentChildNode?.precondition_scheduling_window;
  const selectedWindowStatus = currentChildNode?.precondition_scheduling_window_status;
  const preconditionExpression = currentChildNode?.precondition ?? '';
  const definitionLocks = normalizeLockTags(selectedNode.locks);
  const rawFlowLocks = currentChildNode?.locks || [];
  // One empty row at a time: a second would have nothing to distinguish it from the first.
  const hasEmptyLockRow = rawFlowLocks.some((tag) => !tag);

  let listOfParameters: EditorParameter[] = parameters.map((parameter) => ({
    ...parameter,
    definitionRuntime: parameter.runtime,
    runtimeOverrideSet: false,
  }));

  if (currentChildNode?.parameters) {
    listOfParameters = parameters.map((currentParam) => {
      // Narrowed by the enclosing `if (currentChildNode?.parameters)`; TS does not carry property narrowing into this callback.
      const matchingParam = (currentChildNode.parameters as FlowParameterOverride[]).find((param) => param.name === currentParam.name);
      if (matchingParam) {
        const runtimeOverrideSet = Object.prototype.hasOwnProperty.call(matchingParam, 'runtime');
        return {
          ...currentParam,
          value: matchingParam.value,
          parent_parameter_out: matchingParam.parent_parameter_out,
          runtime: runtimeOverrideSet ? matchingParam.runtime === true || matchingParam.runtime === 'true' : currentParam.runtime,
          definitionRuntime: currentParam.runtime,
          runtimeOverrideSet,
          inout: currentParam.inout, // Keep the inout from the node definition
        };
      }
      return {
        ...currentParam,
        definitionRuntime: currentParam.runtime,
        runtimeOverrideSet: false,
      };
    });
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
    }
  };

  return (
    <>
      <Form.Group className="mb-2">
        <Form.Label>Node ID</Form.Label>
        <Form.Control
          type="text"
          value={editingNodeId}
          onChange={(e) => {
            setEditingNodeId(e.target.value);
            setNodeIdError('');
          }}
          onBlur={handleNodeIdBlur}
          onKeyDown={(e: React.KeyboardEvent) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              (e.target as HTMLInputElement).blur();
            }
          }}
          isInvalid={!!nodeIdError}
        />
        {nodeIdError && <Form.Text className="text-danger">{nodeIdError}</Form.Text>}
      </Form.Group>
      <Form.Group className="mb-2">
        <Form.Label>Label</Form.Label>
        <Form.Control
          type="text"
          value={currentChildNode?.label ?? ''}
          placeholder={selectedNode.name || ''}
          onChange={(e) => onLabelChange(e, id)}
          onKeyDown={handleKeyDown}
        />
      </Form.Group>
      {selectedNode.name && (
        <Form.Group className="mb-2">
          <Form.Label>Name</Form.Label>
          <Form.Control type="text" value={selectedNode.name} disabled />
        </Form.Group>
      )}
      <Form.Group className="mb-2">
        <Form.Label>Description</Form.Label>
        <Form.Control
          as="textarea"
          rows={2}
          value={currentChildNode?.description ?? ''}
          placeholder={selectedNode.description || ''}
          onChange={(e) => onDescriptionChange(e, id)}
          onKeyDown={handleKeyDown}
        />
      </Form.Group>
      {listOfParameters &&
        listOfParameters.map((parameter, index) => {
          const { name, value, default_expression, inout, parent_parameter_out, definitionRuntime, runtime, runtimeOverrideSet } = parameter;
          const definitionRuntimeValue = definitionRuntime === true;
          const runtimeValue = runtime === true;
          const runtimeOverrideValue = runtimeOverrideSet ? String(runtimeValue) : 'inherit';
          const expressionValue = value !== undefined && value !== null && value !== '' ? value : default_expression;
          const showRuntimeWarning =
            !runtimeValue &&
            typeof expressionValue === 'string' &&
            expressionValue.trim().startsWith('=') &&
            /\bNode\.(Sibling|output|outputDetails)\b|\bSibbling_/i.test(expressionValue);

          return (
            <Form.Group key={`${name}-${index}-${id}`} className="flow-parameter-group mb-3" controlId={`${name}-${index}`}>
              <Form.Label className="fw-semibold mb-2">{name}</Form.Label>
              <Form.Group className="mb-2" controlId={`${name}-${index}-value`}>
                <Form.Label className="small mb-1">Value</Form.Label>
                <Form.Control
                  type="text"
                  name={name ?? undefined}
                  value={value ?? ''}
                  placeholder={!value ? (default_expression ?? undefined) : undefined}
                  onChange={(e) => onChange(e, name, id)}
                  onKeyDown={handleKeyDown}
                />
              </Form.Group>
              <Form.Group className="mb-2" controlId={`${name}-${index}-definition-runtime`}>
                <div className="d-flex align-items-center gap-2 small">
                  <span className="text-body-secondary">Definition runtime:</span>
                  <span className={`badge ${definitionRuntimeValue ? 'bg-success' : 'bg-secondary'}`}>{String(definitionRuntimeValue)}</span>
                </div>
              </Form.Group>
              <Form.Group className="mb-2" controlId={`${name}-${index}-runtime-override`}>
                <Form.Label className="small mb-1">Runtime override</Form.Label>
                <Form.Select
                  size="sm"
                  value={runtimeOverrideValue}
                  onChange={(e) => {
                    const nextRuntime = e.target.value;
                    onRuntimeChange(name, id, nextRuntime === 'inherit' ? undefined : nextRuntime === 'true');
                  }}
                >
                  <option value="inherit">Inherit from definition</option>
                  <option value="true">Override: true</option>
                  <option value="false">Override: false</option>
                </Form.Select>
                <Form.Text muted>Effective runtime: {String(runtimeValue)}</Form.Text>
                {showRuntimeWarning && (
                  <div className="flow-runtime-warning small mb-0 mt-2">
                    This expression may need runtime=true. With runtime=false it is evaluated when flow children are inserted, before sibling or output values
                    may exist.
                  </div>
                )}
              </Form.Group>
              {(inout === 'inout' || inout === 'out') && (
                <Form.Group className="mb-0" controlId={`${name}-${index}-parent-parameter-out`}>
                  <Form.Label className="small mb-1">Update parent parameter</Form.Label>
                  <Form.Control
                    type="text"
                    name={name ?? undefined}
                    value={parent_parameter_out ?? ''}
                    placeholder={''}
                    onChange={(e) => onParentParameterOutChange(e, name, id)}
                    onKeyDown={handleKeyDown}
                  />
                </Form.Group>
              )}
            </Form.Group>
          );
        })}

      <Queues selectedValue={selectedQueue} defaultValue={default_queue_path} onChange={(e) => onQueueChange(e, id)} />

      <Form.Group className="mb-2" controlId="formSchedulingWindows">
        <Form.Label>Precondition Schedule Windows</Form.Label>
        <EditSchedulingWindowReference
          placeholder="No scheduling window"
          value={parseSchedulingWindowPath(selectedWindow)}
          onChange={(w) => onWindowChange(formatSchedulingWindowPath(w), id)}
        />
      </Form.Group>

      {selectedWindow && <ScheduleStatus selectedValue={selectedWindowStatus} defaultValue={null} onChange={(e) => onWindowStatusChange(e, id)} />}

      <Form.Group className="mb-2" controlId="preconditionExpression">
        <Form.Label>Precondition expression</Form.Label>
        <EditStringWithHelp
          name="precondition_expression"
          value={preconditionExpression}
          placeholder='e.g. "{{test_flag}}" == "run"'
          onChange={(e) => onPreconditionExpressionChange(e, id)}
          onKeyDown={handleKeyDown}
          helpTitle="Precondition expression help"
        >
          <p className="mb-2">
            JEXL syntax. Use <code>==</code> / <code>!=</code> (not <code>===</code>).
          </p>
          <p className="mb-2">
            String example: <code>{'"{{test_flag}}" == "run"'}</code>
          </p>
          <p className="mb-0">
            Boolean/number examples: <code>{'{{is_enabled}} == true'}</code>, <code>{'{{amount}} > 0'}</code>
          </p>
        </EditStringWithHelp>
      </Form.Group>

      <Form.Group className="mb-2" controlId="formScheduleSchedulingWindow">
        <Form.Label>Schedule Scheduling Window</Form.Label>
        <EditSchedulingWindowReference
          placeholder="No scheduling window"
          value={parseSchedulingWindowPath(selectedScheduleWindow)}
          onChange={(w) => onScheduleSchedulingWindowChange(formatSchedulingWindowPath(w), id)}
        />
      </Form.Group>

      {selectedScheduleWindow && (
        <ScheduleScheduleStatus
          selectedValue={selectedScheduleWindowStatus ?? null}
          defaultValue="open"
          onChange={(e) => onScheduleSchedulingWindowStatusChange(e, id)}
        />
      )}

      <ScheduleStartTime selectedValue={selectedScheduleStartTime} onChange={(e) => onScheduleStartTimeChange(e, id)} />

      <Form.Group className="mt-3">
        <Form.Check
          type="checkbox"
          id={`submit-disabled-${id}`}
          label="Disabled by default"
          checked={currentChildNode?.submit_disabled === true}
          onChange={(e) => onSubmitDisabledChange(e, id)}
          title="This node will be automatically skipped when the flow runs"
        />
      </Form.Group>

      {onSubmitAndForgetChange && (
        <Form.Group className="mt-3">
          <Form.Check
            type="checkbox"
            id={`submit-and-forget-${id}`}
            label="Submit and forget"
            checked={isSubmitAndForgetPlacement}
            onChange={(e) => onSubmitAndForgetChange(e, id)}
            title="Detach-submit the referenced definition as a root instance; the flow step completes immediately"
          />
        </Form.Group>
      )}

      {handles.length > 0 && onHandleStatusActionChange && (
        <Form.Group className="mt-3">
          <Form.Label>Status handler (when flow reaches this node via handle)</Form.Label>
          {handles.map((handle) => (
            <Form.Group key={handle.id} className="mb-2">
              <Form.Label style={{ fontSize: '0.7em' }}>{handle.label || handle.id}</Form.Label>
              <Form.Select
                value={currentChildNode?.handle_status_actions?.[handle.id] ?? 'nothing'}
                onChange={(e) => onHandleStatusActionChange(handle.id, e.target.value, id)}
              >
                {HANDLE_STATUS_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          ))}
        </Form.Group>
      )}

      {onWaitEventAdd && (
        <Form.Group className="mt-3">
          <Form.Label className="d-block mb-2">Wait Events</Form.Label>
          {(currentChildNode?.wait_events || []).map((event, index) => (
            <div key={index} className="d-flex flex-column gap-2 mb-2 p-2 border rounded">
              <EditEventPathReference
                placeholder="Select event..."
                value={event.path ?? null}
                onChange={(path) => onWaitEventChange(id, index, 'path', path)}
              />
              <div className="d-flex align-items-center gap-2">
                <Form.Check
                  type="checkbox"
                  label="Auto Clear"
                  checked={event.auto_clear ?? true}
                  onChange={(e) => onWaitEventChange(id, index, 'auto_clear', e.target.checked)}
                  className="text-nowrap"
                />
                <Form.Control
                  type="text"
                  value={event.precondition ?? ''}
                  onChange={(e) => onWaitEventChange(id, index, 'precondition', e.target.value || null)}
                  className="flex-fill"
                  placeholder="Precondition: true or =expr"
                  onKeyDown={(e: React.KeyboardEvent) => {
                    if (e.key === 'Enter') e.preventDefault();
                  }}
                />
                <Button variant="outline-secondary" size="sm" onClick={() => onWaitEventRemove(id, index)}>
                  X
                </Button>
              </div>
            </div>
          ))}
          <Button className="mt-2" size="sm" variant="outline-primary" onClick={() => onWaitEventAdd(id)}>
            Add wait event
          </Button>
        </Form.Group>
      )}

      {onRaiseEventAdd && !isSubmitAndForgetPlacement && (
        <Form.Group className="mt-3">
          <Form.Label className="d-block mb-2">Raise Events</Form.Label>
          {(currentChildNode?.raise_events || []).map((event, index) => (
            <div key={index} className="d-flex flex-column gap-2 mb-2 p-2 border rounded">
              <EditEventPathReference
                placeholder="Select event..."
                value={event.path ?? null}
                onChange={(path) => onRaiseEventChange(id, index, 'path', path)}
              />
              {/* Comment gets its own line: three controls abreast are unreadable at the panel's default width. */}
              <Form.Control
                type="text"
                value={event.raiser_comment ?? ''}
                onChange={(e) => onRaiseEventChange(id, index, 'raiser_comment', e.target.value)}
                placeholder="Comment or =expression"
                onKeyDown={(e: React.KeyboardEvent) => {
                  if (e.key === 'Enter') e.preventDefault();
                }}
              />
              <div className="d-flex align-items-center gap-2">
                <Form.Control
                  type="text"
                  value={event.precondition ?? ''}
                  onChange={(e) => onRaiseEventChange(id, index, 'precondition', e.target.value)}
                  className="flex-fill"
                  placeholder="Precondition: true or =expr"
                  onKeyDown={(e: React.KeyboardEvent) => {
                    if (e.key === 'Enter') e.preventDefault();
                  }}
                />
                <Button variant="outline-secondary" size="sm" onClick={() => onRaiseEventRemove(id, index)}>
                  X
                </Button>
              </div>
            </div>
          ))}
          <Button className="mt-2" size="sm" variant="outline-primary" onClick={() => onRaiseEventAdd(id)}>
            Add raise event
          </Button>
        </Form.Group>
      )}

      {definitionLocks.length > 0 && (
        <Form.Group className="mt-3">
          <Form.Label className="d-block mb-1">
            {t('flow-editor.definition-locks')} <small className="text-muted">({t('flow-editor.inherited-read-only')})</small>
          </Form.Label>
          {definitionLocks.map((tag: string, i: number) => (
            <div key={`${tag}-${i}`} className="mb-1">
              <span className="badge bg-light text-muted border">{formatLockTag(tag)}</span>
            </div>
          ))}
          <Form.Text className="text-muted d-block mt-1">{t('flow-editor.definition-locks-help')}</Form.Text>
        </Form.Group>
      )}

      {onLockAdd && !isSubmitAndForgetPlacement && (
        <Form.Group className="mt-3">
          <Form.Label className="d-block mb-2">
            {t('flow-editor.flow-node-locks')} <small className="text-muted">({t('flow-editor.additive')})</small>
          </Form.Label>
          {/*
            A node's locks are a set, and the server canonicalizes the array on save — so two rows
            naming the same lock collapse into one rather than being rejected, and the picker does
            not exclude what is already picked. Nor does it exclude the definition's own locks: the
            two arrays are separate, and whether holding both matters is the server's call, not a
            rule to guess at here.
          */}
          {(currentChildNode?.locks || []).map((tag, index) => (
            <div key={index} className="d-flex align-items-center mb-2">
              <div className="flex-grow-1 me-2" style={{ minWidth: 0 }}>
                <EditLockReference
                  value={tag || null}
                  onChange={(value) => onLockChange(id, index, value)}
                  placeholder="Select lock..."
                  zoneName={state.zone_name || undefined}
                />
              </div>
              <Button variant="outline-secondary" size="sm" onClick={() => onLockRemove(id, index)}>
                X
              </Button>
            </div>
          ))}
          <Form.Text className="text-muted d-block">{t('flow-editor.flow-node-locks-additive-help')}</Form.Text>
          <Button className="mt-2" size="sm" variant="outline-primary" onClick={() => onLockAdd(id)} disabled={hasEmptyLockRow}>
            {t('flow-editor.add-lock')}
          </Button>
        </Form.Group>
      )}
    </>
  );
};

const normalizeChildNode = (node: FlowChildNode): FlowChildNode => ({
  ...node,
  submit_disabled: node.submit_disabled === true,
  submit_and_forget: node.submit_and_forget === true,
  data: {
    ...node.data,
    submit_disabled: node.submit_disabled === true,
    submit_and_forget: node.submit_and_forget === true,
  },
});

const mapFetchedChildNodes = (childNodes: readonly FlowDefinitionNode[]): FlowChildNode[] =>
  childNodes.map((node) => {
    const extractedZoneName = node.node_definition_path?.split('|')[0];
    const scheduleStartTime = typeof node.schedule_start_time === 'string' ? node.schedule_start_time.substring(0, 5) : (node.schedule_start_time ?? null);
    return normalizeChildNode({
      ...node,
      // Nullable columns that default to 0; the editor always saves both coordinates.
      position: { x: node.position.x ?? 0, y: node.position.y ?? 0 },
      // Null when the placed definition no longer exists (a LEFT JOIN); react-flow then draws its default node.
      type: node.type ?? undefined,
      schedule_start_time: scheduleStartTime,
      node_definition_name: node.node_definition_name,
      zone_name: node.zone_name || extractedZoneName,
      submit_and_forget: node.submit_and_forget === true,
      data: {
        label: node.label || node.node_definition_name,
        handles: node.handles,
        description: node.description || null,
        submit_and_forget: node.submit_and_forget === true,
      },
      description: node.flow_description ?? null,
    });
  });

// The record the property panel shows for its node. Data, not selection state: it is fetched for the node the selection
// targets, and shown only while its `id` is still that target.
type PanelNodeRecord =
  | { readonly kind: 'loading'; readonly id: string }
  | { readonly kind: 'loaded'; readonly id: string; readonly record: FlowSelectedNodeRef }
  | { readonly kind: 'unavailable'; readonly id: string }
  | { readonly kind: 'failed'; readonly id: string; readonly error: Error };

const NO_PANEL_TARGET: FlowPanelTarget = { kind: 'none' };

// Generic over the host's state: Flow reads and writes only the `FlowState` part and spreads the rest through.
interface FlowProps<S extends FlowState> {
  state: S;
  onChange: unknown;
  setState: (updater: S | ((prev: S) => S)) => void;
  // Loads the record the property panel shows for a node. Defaults to the node's placed definition (the definition editor);
  // the instance viewer passes its own. Resolves null when the node has nothing to show.
  loadNodeDetails?: ((node: Node) => Promise<FlowSelectedNodeRef | null>) | null;
  showNodeLibrary?: boolean;
  disabled?: boolean;
  onSubflowStateChange?: ((isInsideSubflow: boolean) => void) | null;
  onNavChange?: ((nav: ReactNode) => void) | null;
  onFetchSubflow?: ((nodeId: number) => Promise<{ child_nodes: FlowChildNode[]; child_edges: FlowEdge[] } | null>) | null;
  onExpandNode?: ((nodeId: string) => void) | null;
}

const Flow = <S extends FlowState>({
  state,
  onChange,
  setState,
  loadNodeDetails = null,
  showNodeLibrary = true,
  disabled = false,
  onSubflowStateChange = null,
  onNavChange = null,
  onFetchSubflow = null,
  onExpandNode = null,
}: FlowProps<S>) => {
  const { child_nodes = [], child_edges = [] } = state;
  const { fitView } = useReactFlow();

  const dispatch = useAppDispatch();
  const [searchTerm, setSearchTerm] = useState<string>('');
  const minimumSearchLength = 3;
  const searchTimeoutRef = useRef<number | null>(null);
  const reactFlowInstance = useReactFlow();

  const [searchInput, setSearchInput] = useState('');

  // Collapse state for the resizable side panes (rail vs full pane).
  const [libraryCollapsed, setLibraryCollapsed] = useState(false);
  const [panelMode, setPanelMode] = useState<FlowPanelMode>('closed');

  // Side-pane widths in pixels; the canvas (flex:1) takes the rest.
  const [libraryWidth, setLibraryWidth] = useState(FLOW_PANE_LIMITS.library.default);
  const [panelWidth, setPanelWidth] = useState(FLOW_PANE_LIMITS.panel.default);

  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [libraryNodes, setLibraryNodes] = useState<FlowLibraryNode[]>([]);
  const [definitionLocksByNodeDefinitionId, setDefinitionLocksByNodeDefinitionId] = useState<Record<string, string[]>>({});

  const hasMore = currentPage < totalPages;

  interface SubflowLevel {
    name: string;
    node_definition_id: number;
    child_nodes: FlowChildNode[];
    child_edges: FlowEdge[];
  }

  const [subflowStack, setSubflowStack] = useState<SubflowLevel[]>([]);
  const [isNavigating, setIsNavigating] = useState(false);

  const isInsideSubflow = subflowStack.length > 0;
  const currentSubflow = isInsideSubflow ? subflowStack[subflowStack.length - 1] : null;
  const displayedNodes = currentSubflow ? currentSubflow.child_nodes : child_nodes;
  const displayedEdges = currentSubflow ? currentSubflow.child_edges : child_edges;

  const rememberDefinitionLocks = useCallback((nodeDefinitionId: number | string | null | undefined, locks: unknown) => {
    if (!nodeDefinitionId) return;
    const key = String(nodeDefinitionId);
    const normalizedLocks = normalizeLockTags(locks);

    setDefinitionLocksByNodeDefinitionId((prev) => {
      if (prev[key] !== undefined && lockTagsAreEqual(prev[key], normalizedLocks)) {
        return prev;
      }
      return { ...prev, [key]: normalizedLocks };
    });
  }, []);

  const displayedNodeDefinitionIds = useMemo<string[]>(() => {
    const ids = displayedNodes
      .map((node) => (node.node_definition_id != null ? String(node.node_definition_id) : null))
      .filter((nodeDefinitionId): nodeDefinitionId is string => Boolean(nodeDefinitionId));

    return Array.from(new Set(ids));
  }, [displayedNodes]);

  useEffect(() => {
    let isCancelled = false;
    const missingNodeDefinitionIds = displayedNodeDefinitionIds.filter((nodeDefinitionId) => definitionLocksByNodeDefinitionId[nodeDefinitionId] === undefined);

    if (missingNodeDefinitionIds.length === 0) return;

    missingNodeDefinitionIds.forEach(async (nodeDefinitionId) => {
      try {
        const response = await api.get(`/node_definitions/${nodeDefinitionId}`);
        if (isCancelled) return;
        rememberDefinitionLocks(nodeDefinitionId, response?.data?.locks);
      } catch (error) {
        if (!isCancelled) {
          rememberDefinitionLocks(nodeDefinitionId, []);
        }
        console.error('Error fetching node definition locks:', error);
      }
    });

    return () => {
      isCancelled = true;
    };
  }, [definitionLocksByNodeDefinitionId, displayedNodeDefinitionIds, rememberDefinitionLocks]);

  const nodesWithDefinitionLocks = useMemo(
    () =>
      displayedNodes.map((node) => {
        const nodeDefinitionId = node.node_definition_id;
        const knownLocks = nodeDefinitionId != null ? definitionLocksByNodeDefinitionId[String(nodeDefinitionId)] : undefined;
        const currentLocks = normalizeLockTags(node.data?.definition_locks);
        const nextLocks = knownLocks ?? currentLocks;

        if (lockTagsAreEqual(currentLocks, nextLocks)) return node;

        return {
          ...node,
          data: {
            ...node.data,
            definition_locks: nextLocks,
          },
        };
      }),
    [definitionLocksByNodeDefinitionId, displayedNodes]
  );
  const showLibrary = showNodeLibrary && !isInsideSubflow;

  // What the property panel shows is derived from the canvas selection; only its open/collapsed mode is its own state. A
  // subflow level has no panel.
  const panelTargetNow = isInsideSubflow ? NO_PANEL_TARGET : panelTarget(child_nodes, child_edges);
  const targetNodeId = panelTargetNow.kind === 'node' ? panelTargetNow.id : null;

  const loadDefinitionDetails = useCallback(
    async (node: Node): Promise<FlowSelectedNodeRef | null> => {
      const node_definition_id = (node as FlowChildNode).node_definition_id;
      if (!node_definition_id) return null;
      // Shown, never saved: the panel edits the placement, and the record is not persisted.
      const details = acceptDegraded(await getPlacedNodeDefinition(node_definition_id));
      rememberDefinitionLocks(node_definition_id, details.locks);
      return { ...details, id: node.id };
    },
    [rememberDefinitionLocks]
  );
  const loadPanelNode = loadNodeDetails ?? loadDefinitionDetails;

  const [panelNode, setPanelNode] = useState<PanelNodeRecord | null>(null);
  const panelNodeRef = useRef(panelNode);
  panelNodeRef.current = panelNode;
  useEffect(() => {
    if (!targetNodeId) return;
    const current = panelNodeRef.current;
    if (current?.id === targetNodeId && (current.kind === 'loaded' || current.kind === 'unavailable')) return;
    const node = reactFlowInstance.getNode(targetNodeId);
    if (!node) return;
    let superseded = false;
    setPanelNode({ kind: 'loading', id: targetNodeId });
    loadPanelNode(node).then(
      (record) => {
        if (!superseded) setPanelNode(record ? { kind: 'loaded', id: targetNodeId, record } : { kind: 'unavailable', id: targetNodeId });
      },
      (error: unknown) => {
        if (!superseded) setPanelNode({ kind: 'failed', id: targetNodeId, error: toError(error) });
      }
    );
    return () => {
      superseded = true;
    };
  }, [targetNodeId, loadPanelNode, reactFlowInstance]);
  const panelNodeRecord = panelNode && panelNode.id === targetNodeId ? panelNode : null;

  const { selectedEdge, panelTitle, handleLoopEdgeRestartChange } = useFlowEdgeSelection({
    setState,
    child_edges,
    selectedEdgeId: panelTargetNow.kind === 'edge' ? panelTargetNow.id : null,
    selectedNodeName: panelNodeRecord?.kind === 'loaded' ? panelNodeRecord.record.name : null,
  });
  const libraryExpanded = showLibrary && !libraryCollapsed;
  const panelOpen = panelMode !== 'closed' && !isInsideSubflow;

  // Notify parent when subflow state changes
  useEffect(() => {
    if (onSubflowStateChange) onSubflowStateChange(isInsideSubflow);
  }, [isInsideSubflow, onSubflowStateChange]);

  const breadcrumbItems = useMemo(() => [{ name: state.name || 'Root' }, ...subflowStack.map((level) => ({ name: level.name }))], [state.name, subflowStack]);

  const navigateIntoSubflow = useCallback(
    async (nodeId: number, nodeName: string) => {
      setIsNavigating(true);

      try {
        let childNodes: FlowChildNode[] | undefined;
        let childEdges: FlowEdge[] | undefined;

        if (onFetchSubflow) {
          const result = await onFetchSubflow(nodeId);
          if (result) {
            childNodes = result.child_nodes;
            childEdges = result.child_edges;
          }
        } else {
          const graph = acceptDegraded(await getNodeDefinitionGraph(nodeId));
          childNodes = mapFetchedChildNodes(graph.child_nodes);
          childEdges = [...graph.child_edges];
        }

        if (childNodes) {
          setSubflowStack((prev) => [
            ...prev,
            {
              name: nodeName,
              node_definition_id: nodeId,
              child_nodes: childNodes,
              child_edges: childEdges || [],
            },
          ]);

          setTimeout(() => fitView(), 100);
        }
      } catch (error) {
        notify.error(`Could not open subflow ${nodeName}: ${toError(error).message}`);
      } finally {
        setIsNavigating(false);
      }
    },
    [fitView, onFetchSubflow]
  );

  const navigateToBreadcrumb = useCallback(
    (index: number) => {
      if (index === 0) {
        setSubflowStack([]);
      } else {
        setSubflowStack((prev) => prev.slice(0, index));
      }

      setTimeout(() => fitView(), 100);
    },
    [fitView]
  );

  const navigateBack = useCallback(() => {
    setSubflowStack((prev) => prev.slice(0, -1));
    setTimeout(() => fitView(), 100);
  }, [fitView]);

  const subflowNavContextValue = useMemo(
    () => ({
      navigateIntoSubflow,
      isInsideSubflow,
      onExpandNode: onExpandNode || null,
    }),
    [navigateIntoSubflow, isInsideSubflow, onExpandNode]
  );

  useEffect(() => {
    if (!onNavChange) return;
    if (!isInsideSubflow) {
      onNavChange(null);
      return;
    }
    onNavChange(
      <div className="subflow-nav-bar d-inline-flex align-items-center ms-3">
        <Button variant="outline-secondary" size="sm" onClick={navigateBack} className="me-3">
          ← Back
        </Button>
        <SubflowBreadcrumbs items={breadcrumbItems} onNavigate={navigateToBreadcrumb} />
      </div>
    );
  }, [isInsideSubflow, breadcrumbItems, navigateBack, navigateToBreadcrumb, onNavChange]);

  const onSubflowNodesChange = useCallback((changes: NodeChange[]) => {
    setSubflowStack((prev) => {
      if (prev.length === 0) return prev;
      const stack = [...prev];
      const last = stack[stack.length - 1];
      stack[stack.length - 1] = {
        ...last,
        // react-flow's applyNodeChanges returns its own Node type (dropping our fields and our narrower `style`); it passes both through unchanged.
        child_nodes: applyNodeChanges(changes, last.child_nodes) as FlowChildNode[],
      };
      return stack;
    });
  }, []);

  const onSubflowEdgesChange = useCallback((changes: EdgeChange[]) => {
    setSubflowStack((prev) => {
      if (prev.length === 0) return prev;
      const stack = [...prev];
      const last = stack[stack.length - 1];
      stack[stack.length - 1] = {
        ...last,
        child_edges: applyEdgeChanges(changes, last.child_edges),
      };
      return stack;
    });
  }, []);

  const fetchNodes = useCallback(
    async (page: number) => {
      return dispatch(
        fetchCustomerNodeDefinitions({
          limit: 15,
          type: 'All',
          // Name order, which the backend sorts case-insensitively.
          sortColumn: 'name',
          sortOrder: 'ASC',
          search: searchTerm,
          page,
        })
      );
    },
    [dispatch, searchTerm]
  );

  useEffect(() => {
    if (!showNodeLibrary) return;

    const getNodeLibrary = async () => {
      try {
        const response = await fetchNodes(1);

        if (response?.payload) {
          setLibraryNodes(response.payload.nodes || []);
          setTotalPages(response.payload.pagination?.total_pages || 1);
          setCurrentPage(1);
        }
      } catch (error) {
        console.error('Error fetching node definitions:', error);
      }
    };
    getNodeLibrary();
  }, [fetchNodes, showNodeLibrary]);

  const handleLoadMore = useCallback(async () => {
    if (isLoadingMore || !hasMore) return;

    setIsLoadingMore(true);
    const nextPage = currentPage + 1;

    try {
      const response = await fetchNodes(nextPage);

      if (response?.payload?.nodes) {
        setLibraryNodes((prev) => [...prev, ...response.payload.nodes]);
        setCurrentPage(nextPage);
      }
    } catch (error) {
      console.error('Error fetching more node definitions:', error);
    } finally {
      setIsLoadingMore(false);
    }
  }, [currentPage, fetchNodes, hasMore, isLoadingMore]);

  const styles = {
    width: '100%',
    height: '100%',
  };

  // ReactFlow Handlers
  // A selection opens a closed panel (see `panelModeOnSelection`); what the panel shows is derived from the selection itself.
  const onSelectionChange = useCallback(
    ({ nodes, edges }: OnSelectionChangeParams) => {
      if (isInsideSubflow) return;
      const target = panelTarget(nodes, edges);
      setPanelMode((mode) => panelModeOnSelection(mode, target));
    },
    [isInsideSubflow]
  );

  const onConnect = useCallback(
    (params: Connection) => {
      // Typed nullable, but react-flow calls onConnect only once both ends are set.
      const { source, target } = params;
      if (source === null || target === null) return;
      return setState((prev) => {
        const cleanEdgeId = (edgeId: string) => (edgeId.startsWith('reactflow__edge-') ? edgeId.replace('reactflow__edge-', '') : edgeId);
        const id = cleanEdgeId(uuidv4()); // use UUID without `reactflow__edge-` prefix

        const sourceNode = reactFlowInstance.getNode(source);
        const targetNode = reactFlowInstance.getNode(target);

        // Self-loops must be loopback (not default): the X-position heuristic
        // treats equal positions as forward and would deadlock scheduling.
        const isSelfLoop = source === target;
        const isTargetLeftOfSource = targetNode !== undefined && sourceNode !== undefined && targetNode.position.x < sourceNode.position.x;
        const edgeType = isSelfLoop || isTargetLeftOfSource ? 'loopback' : 'default';
        const newEdge = {
          id,
          source,
          target,
          sourceHandle: params.sourceHandle, // Store the source handle ID
          targetHandle: params.targetHandle, // Store the target handle ID
          type: edgeType,
        };

        const addedEdges = addEdge(newEdge, prev.child_edges);

        return {
          ...prev,
          child_edges: addedEdges,
        };
      });
    },
    [reactFlowInstance, setState]
  );

  const onNodesChange = useCallback(
    (changes: NodeChange[]) => {
      return setState((prev) => {
        const changedNodes = applyNodeChanges(changes, prev.child_nodes);
        return {
          ...prev,
          child_nodes: changedNodes,
        };
      });
    },
    [setState]
  );

  const onEdgesChange = useCallback(
    (changes: EdgeChange[]) => {
      return setState((prev) => {
        const changedEdges = applyEdgeChanges(changes, prev.child_edges);
        return {
          ...prev,
          child_edges: changedEdges,
        };
      });
    },
    [setState]
  );

  const onDrop = (event: DragEvent<HTMLDivElement>) => {
    const id = 0;
    const { screenToFlowPosition } = reactFlowInstance;
    const { child_nodes } = state;
    const selectedNode: FlowLibraryNode = JSON.parse(event.dataTransfer.getData('application/reactflow'));
    const { type, name, description, handles, zone_name, hold_on_submit, node_definition_id } = selectedNode;
    const definitionLocks = normalizeLockTags(selectedNode.locks);

    event.preventDefault();

    const position = screenToFlowPosition({
      x: event.clientX,
      y: event.clientY,
    });

    let max = child_nodes.reduce((acc: number, value) => {
      // String(): the edited graph's ids are not checked (see FlowChildNode); an id-less placement yields NaN, which never wins.
      const idNum = parseInt(String(value.id).replace('node-', ''), 10);
      return idNum > acc ? idNum : acc;
    }, 0);
    // Generate a unique ID for the new node
    max += 1;

    const newNodeId = `node-${max}`;
    const newNode = normalizeChildNode({
      id: newNodeId,
      node_definition_id,
      node_definition_name: name,
      position,
      zone_name,
      type,
      hold_on_submit: !!hold_on_submit,
      data: {
        label: name,
        handles,
        description: description || null,
        definition_locks: definitionLocks,
      },
      label: null,
      description: null,
      precondition: null,
      precondition_scheduling_window: null,
      precondition_scheduling_window_status: null,
      schedule_scheduling_window: null,
      schedule_scheduling_window_status: null,
      schedule_start_time: null,
    });

    rememberDefinitionLocks(node_definition_id, definitionLocks);

    return setState((prev) => ({
      ...prev,
      child_nodes: child_nodes.concat(newNode),
    }));
  };

  const onDragOver = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
  };

  const onDragStart = (event: DragEvent<HTMLDivElement>, nodeType: FlowLibraryNode) => {
    return event.dataTransfer.setData('application/reactflow', JSON.stringify(nodeType));
  };

  // Click handlers
  const handleChildNodeChange = (event: ChangeEvent<FormControlElement>, name: string | null, nodeId: string) => {
    const { value } = event.target;

    const mappedNodes = child_nodes.map((node) => {
      if (node.id === nodeId) {
        return {
          ...node,
          // A nullable column; the backend stores [] for a null, so this guards older rows only.
          parameters: updateParameterOverride(node.parameters ?? [], name, { value }),
        };
      }

      return node;
    });

    return setState((prev) => ({
      ...prev,
      child_nodes: mappedNodes,
    }));
  };

  const handleRuntimeChange = (name: string | null, nodeId: string, runtime: boolean | undefined) => {
    const mappedNodes = child_nodes.map((node) => {
      if (node.id === nodeId) {
        return {
          ...node,
          parameters: updateParameterOverride(node.parameters ?? [], name, { runtime }),
        };
      }

      return node;
    });

    return setState((prev) => ({
      ...prev,
      child_nodes: mappedNodes,
    }));
  };

  const handleNodeIdChange = (oldId: string, newId: string) => {
    const mappedNodes = child_nodes.map((node) => {
      if (node.id === oldId) {
        return { ...node, id: newId, data: { ...node.data, id: newId } };
      }
      return node;
    });

    const mappedEdges = child_edges.map((edge) => {
      let updated = edge;
      if (edge.source === oldId) {
        updated = { ...updated, source: newId };
      }
      if (edge.target === oldId) {
        updated = { ...updated, target: newId };
      }
      return updated;
    });

    setState((prev) => ({
      ...prev,
      child_nodes: mappedNodes,
      child_edges: mappedEdges,
    }));
    // The node keeps its record: only its canvas id changed, not the definition the record describes.
    setPanelNode((prev) => (prev?.kind === 'loaded' && prev.id === oldId ? { ...prev, id: newId, record: { ...prev.record, id: newId } } : prev));
  };

  const handleLabelChange = (event: ChangeEvent<FormControlElement>, nodeId: string) => {
    const { value } = event.target;

    const mappedNodes = child_nodes.map((node) => {
      if (node.id !== nodeId) return node;
      return {
        ...node,
        label: value || null,
        data: { ...node.data, label: value || node.node_definition_name },
      };
    });

    return setState((prev) => ({
      ...prev,
      child_nodes: mappedNodes,
    }));
  };

  const handleParentParameterOutChange = (event: ChangeEvent<FormControlElement>, name: string | null, nodeId: string) => {
    const { value } = event.target;

    const mappedNodes = child_nodes.map((node) => {
      if (node.id === nodeId) {
        return {
          ...node,
          parameters: updateParameterOverride(node.parameters ?? [], name, {
            parent_parameter_out: value,
          }),
        };
      }

      return node;
    });

    return setState((prev) => ({
      ...prev,
      child_nodes: mappedNodes,
    }));
  };

  const handleQueueChange = (event: ChangeEvent<HTMLSelectElement>, nodeId: string) => {
    const { value } = event.target;

    const mappedNodes = child_nodes.map((node) => {
      if (node.id === nodeId) {
        return { ...node, queue_path: value || null };
      }
      return node;
    });

    return setState((prev) => ({
      ...prev,
      child_nodes: mappedNodes,
    }));
  };

  const handleDescriptionChange = (event: ChangeEvent<FormControlElement>, nodeId: string) => {
    const { value } = event.target;

    const mappedNodes = child_nodes.map((node) => {
      if (node.id !== nodeId) return node;
      return { ...node, description: value || null };
    });

    return setState((prev) => ({
      ...prev,
      child_nodes: mappedNodes,
    }));
  };

  const handleHandleStatusActionChange = (handleId: string, value: string, nodeId: string) => {
    const action = value === 'nothing' ? null : (value as FlowHandleStatusAction);
    const mappedNodes = child_nodes.map((node) => {
      if (node.id === nodeId) {
        const current = node.handle_status_actions || {};
        const next = action === null ? { ...current, [handleId]: undefined } : { ...current, [handleId]: value };
        const cleaned = Object.fromEntries(Object.entries(next).filter(([, v]) => v != null)) as Record<string, FlowHandleStatusAction>;
        return {
          ...node,
          handle_status_actions: Object.keys(cleaned).length > 0 ? cleaned : undefined,
        };
      }
      return node;
    });
    setState((prev) => ({
      ...prev,
      child_nodes: mappedNodes,
    }));
  };

  const handleSearchChange = (event: ChangeEvent<FormControlElement>) => {
    const { value } = event.target;

    setSearchInput(value);

    const next = value.length >= minimumSearchLength ? value : '';
    if (searchTimeoutRef.current) window.clearTimeout(searchTimeoutRef.current);
    searchTimeoutRef.current = window.setTimeout(() => {
      setSearchTerm(next);
    }, 300);
  };

  const handleSearchSubmit = useCallback(() => {
    if (searchTimeoutRef.current) {
      window.clearTimeout(searchTimeoutRef.current);
      searchTimeoutRef.current = null;
    }
    setSearchTerm(searchInput);
  }, [searchInput]);

  const handleWindowChange = (value: string | null, nodeId: string) => {
    const mappedNodes = child_nodes.map((node) => {
      if (node.id !== nodeId) return node;

      const hasWindow = Boolean(value);
      const next: FlowChildNode = {
        ...node,
        precondition_scheduling_window: hasWindow ? value : null,
      };

      if (hasWindow) {
        next.precondition_scheduling_window_status = node.precondition_scheduling_window_status ?? 'open';
      } else {
        next.precondition_scheduling_window_status = null;
      }

      return next;
    });

    return setState((prev) => ({
      ...prev,
      child_nodes: mappedNodes,
    }));
  };

  const handleWindowStatusChange = (event: ChangeEvent<HTMLSelectElement>, nodeId: string) => {
    const { value } = event.target;

    const mappedNodes = child_nodes.map((node) => {
      if (node.id !== nodeId) return node;

      if (!node.precondition_scheduling_window) {
        return { ...node, precondition_scheduling_window_status: null };
      }

      return {
        ...node,
        precondition_scheduling_window_status: value || null,
      };
    });

    return setState((prev) => ({
      ...prev,
      child_nodes: mappedNodes,
    }));
  };

  const handlePreconditionExpressionChange = (event: ChangeEvent<HTMLInputElement>, nodeId: string) => {
    const { value } = event.target;

    const mappedNodes = child_nodes.map((node) => {
      if (node.id !== nodeId) return node;

      return {
        ...node,
        precondition: value.trim() ? value : null,
      };
    });

    return setState((prev) => ({
      ...prev,
      child_nodes: mappedNodes,
    }));
  };

  const handleScheduleSchedulingWindowChange = (value: string | null, nodeId: string) => {
    const mappedNodes = child_nodes.map((node) => {
      if (node.id !== nodeId) return node;

      const hasWindow = Boolean(value);
      const next: FlowChildNode = {
        ...node,
        schedule_scheduling_window: hasWindow ? value : null,
      };

      if (hasWindow) {
        next.schedule_scheduling_window_status = node.schedule_scheduling_window_status ?? 'open';
      } else {
        next.schedule_scheduling_window_status = null;
      }

      return next;
    });

    return setState((prev) => ({
      ...prev,
      child_nodes: mappedNodes,
    }));
  };

  const handleScheduleSchedulingWindowStatusChange = (event: React.ChangeEvent<HTMLSelectElement>, nodeId: string) => {
    const { value } = event.target;

    const mappedNodes = child_nodes.map((node) => {
      if (node.id !== nodeId) return node;

      if (!node.schedule_scheduling_window) {
        return { ...node, schedule_scheduling_window_status: null };
      }

      return {
        ...node,
        schedule_scheduling_window_status: value || null,
      };
    });

    return setState((prev) => ({
      ...prev,
      child_nodes: mappedNodes,
    }));
  };

  const handleScheduleStartTimeChange = (event: React.ChangeEvent<HTMLInputElement>, nodeId: string) => {
    const { value } = event.target;

    const mappedNodes = child_nodes.map((node) => {
      if (node.id !== nodeId) return node;

      return {
        ...node,
        schedule_start_time: value || null,
      };
    });

    return setState((prev) => ({
      ...prev,
      child_nodes: mappedNodes,
    }));
  };

  const handleSubmitDisabledChange = (event: React.ChangeEvent<HTMLInputElement>, nodeId: string) => {
    const { checked } = event.target;

    const mappedNodes = child_nodes.map((node) => {
      if (node.id !== nodeId) return node;

      return {
        ...node,
        submit_disabled: checked,
        data: { ...(node.data || {}), submit_disabled: checked },
      };
    });

    return setState((prev) => ({
      ...prev,
      child_nodes: mappedNodes,
    }));
  };

  const handleSubmitAndForgetChange = (event: React.ChangeEvent<HTMLInputElement>, nodeId: string) => {
    const { checked } = event.target;

    const mappedNodes = child_nodes.map((node) => {
      if (node.id !== nodeId) return node;

      return normalizeChildNode({
        ...node,
        submit_and_forget: checked,
        ...(checked ? { raise_events: [], locks: [] } : {}),
      });
    });

    return setState((prev) => ({
      ...prev,
      child_nodes: mappedNodes,
    }));
  };

  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) window.clearTimeout(searchTimeoutRef.current);
    };
  }, []);

  // Wait event handlers
  const handleWaitEventAdd = (nodeId: string) => {
    const mappedNodes = child_nodes.map((node) => {
      if (node.id !== nodeId) return node;
      const waitEvents = node.wait_events || [];
      return {
        ...node,
        wait_events: [...waitEvents, { path: null, auto_clear: true, precondition: null }],
      };
    });
    setState((prev) => ({ ...prev, child_nodes: mappedNodes }));
  };

  const handleWaitEventRemove = (nodeId: string, eventIndex: number) => {
    const mappedNodes = child_nodes.map((node) => {
      if (node.id !== nodeId) return node;
      const waitEvents = [...(node.wait_events || [])];
      waitEvents.splice(eventIndex, 1);
      return { ...node, wait_events: waitEvents };
    });
    setState((prev) => ({ ...prev, child_nodes: mappedNodes }));
  };

  const handleWaitEventChange = <K extends keyof FlowNodeWaitEvent>(nodeId: string, eventIndex: number, field: K, value: FlowNodeWaitEvent[K]) => {
    const mappedNodes = child_nodes.map((node) => {
      if (node.id !== nodeId) return node;
      const waitEvents = [...(node.wait_events || [])];
      waitEvents[eventIndex] = { ...waitEvents[eventIndex], [field]: value };
      return { ...node, wait_events: waitEvents };
    });
    setState((prev) => ({ ...prev, child_nodes: mappedNodes }));
  };

  // Raise event handlers
  const handleRaiseEventAdd = (nodeId: string) => {
    const mappedNodes = child_nodes.map((node) => {
      if (node.id !== nodeId) return node;
      const raiseEvents = node.raise_events || [];
      return {
        ...node,
        raise_events: [...raiseEvents, { path: null, raiser_comment: '', precondition: '' }],
      };
    });
    setState((prev) => ({ ...prev, child_nodes: mappedNodes }));
  };

  const handleRaiseEventRemove = (nodeId: string, eventIndex: number) => {
    const mappedNodes = child_nodes.map((node) => {
      if (node.id !== nodeId) return node;
      const raiseEvents = [...(node.raise_events || [])];
      raiseEvents.splice(eventIndex, 1);
      return { ...node, raise_events: raiseEvents };
    });
    setState((prev) => ({ ...prev, child_nodes: mappedNodes }));
  };

  const handleRaiseEventChange = <K extends keyof FlowNodeRaiseEvent>(nodeId: string, eventIndex: number, field: K, value: FlowNodeRaiseEvent[K]) => {
    const mappedNodes = child_nodes.map((node) => {
      if (node.id !== nodeId) return node;
      const raiseEvents = [...(node.raise_events || [])];
      raiseEvents[eventIndex] = { ...raiseEvents[eventIndex], [field]: value };
      return { ...node, raise_events: raiseEvents };
    });
    setState((prev) => ({ ...prev, child_nodes: mappedNodes }));
  };

  // Lock handlers
  const handleLockAdd = (nodeId: string) => {
    const mappedNodes = child_nodes.map((node) => {
      if (node.id !== nodeId) return node;
      const locks = node.locks || [];
      return { ...node, locks: [...locks, ''] };
    });
    setState((prev) => ({ ...prev, child_nodes: mappedNodes }));
  };

  const handleLockRemove = (nodeId: string, lockIndex: number) => {
    const mappedNodes = child_nodes.map((node) => {
      if (node.id !== nodeId) return node;
      const locks = [...(node.locks || [])];
      locks.splice(lockIndex, 1);
      return { ...node, locks };
    });
    setState((prev) => ({ ...prev, child_nodes: mappedNodes }));
  };

  const handleLockChange = (nodeId: string, lockIndex: number, value: string | null) => {
    const mappedNodes = child_nodes.map((node) => {
      if (node.id !== nodeId) return node;
      const locks = [...(node.locks || [])];
      locks[lockIndex] = value || '';
      return { ...node, locks };
    });
    setState((prev) => ({ ...prev, child_nodes: mappedNodes }));
  };

  // Closing the panel also clears the selection, so nothing stays selected (and deletable) that the panel does not show.
  const handleCloseEditor = () => {
    setPanelMode('closed');
    return setState((prev) => ({
      ...prev,
      child_nodes: prev.child_nodes.map((node) => (node.selected ? { ...node, selected: false } : node)),
      child_edges: prev.child_edges.map((edge) => (edge.selected ? { ...edge, selected: false } : edge)),
    }));
  };

  const effectiveDisabled = disabled || isInsideSubflow;

  // Self-loops must render as loopback (legacy bad data may be typed default), and every edge needs a registered type.
  const edgesForCanvas = useMemo(() => (displayedEdges || []).map((edge) => coerceEdgeTypeForCanvas(edge)), [displayedEdges]);

  const libraryPane = (
    <div key="library" className="flow-pane node-library-pane" style={{ width: libraryWidth }}>
      <FlowPaneHeader title="Node Library" side="left" onCollapse={() => setLibraryCollapsed(true)} />
      <div className="flow-pane-body node-library">
        <RenderLibraryNodes
          state={state}
          onDragStart={onDragStart}
          onSearch={handleSearchChange}
          onSearchSubmit={handleSearchSubmit}
          searchValue={searchInput}
          onLoadMore={handleLoadMore}
          hasMore={hasMore}
          isLoading={isLoadingMore}
          nodes={libraryNodes}
        />
      </div>
    </div>
  );

  const canvasPane = (
    <div key="canvas" className="flow-pane canvas-pane">
      {!effectiveDisabled && (
        <div className="flow-pane-header">
          <span className="flow-pane-title">Flow edit</span>
        </div>
      )}

      {isNavigating && (
        <div className="subflow-loading-overlay">
          <Spinner animation="border" size="sm" />
        </div>
      )}

      <div
        id="reactflow-wrapper"
        className="flow-pane-canvas"
        onDrop={isInsideSubflow ? undefined : onDrop}
        onDragOver={isInsideSubflow ? undefined : onDragOver}
      >
        <ReactFlow
          style={styles}
          nodes={
            onExpandNode
              ? nodesWithDefinitionLocks.map((n) => ({
                  ...n,
                  data: { ...n.data, onExpandNode },
                }))
              : nodesWithDefinitionLocks
          }
          edges={edgesForCanvas}
          onNodesChange={isInsideSubflow ? onSubflowNodesChange : onNodesChange}
          onEdgesChange={isInsideSubflow ? onSubflowEdgesChange : onEdgesChange}
          onSelectionChange={onSelectionChange}
          onConnect={isInsideSubflow ? undefined : onConnect}
          nodeTypes={nodeTypes}
          edgeTypes={flowEdgeTypes}
          proOptions={{ hideAttribution: true }}
          fitView
        >
          <Background />
          <Controls position="top-left" />
        </ReactFlow>
      </div>
    </div>
  );

  const renderPanelContent = (): ReactNode => {
    if (panelTargetNow.kind === 'edge' && selectedEdge) {
      return <FlowEdgePropertyPanel selectedEdge={selectedEdge} disabled={disabled} onRestartChange={handleLoopEdgeRestartChange} />;
    }
    if (panelTargetNow.kind === 'multiple') {
      return <div className="text-body-secondary p-2">{panelTargetNow.count} items selected.</div>;
    }
    if (panelTargetNow.kind !== 'node') {
      return <div className="text-body-secondary p-2">Select a node or edge to see its properties.</div>;
    }
    if (!panelNodeRecord || panelNodeRecord.kind === 'loading') {
      return (
        <div className="p-2">
          <Spinner animation="border" size="sm" />
        </div>
      );
    }
    if (panelNodeRecord.kind === 'unavailable') {
      return <div className="text-body-secondary p-2">This node has no properties to show.</div>;
    }
    if (panelNodeRecord.kind === 'failed') {
      return <div className="text-danger p-2">Could not load this node: {panelNodeRecord.error.message}</div>;
    }
    if (disabled) {
      return <ReadOnlyProperties state={panelNodeRecord.record} onClose={handleCloseEditor} />;
    }
    return (
      <RenderMiniEditor
        state={state}
        // An editable canvas means the definition editor, whose (default) loader returns a definition record. TS cannot
        // see that through the loader prop; it goes when editor and viewer state are separated.
        selectedNode={panelNodeRecord.record as FlowSelectedNode}
        onChange={handleChildNodeChange}
        onRuntimeChange={handleRuntimeChange}
        onParentParameterOutChange={handleParentParameterOutChange}
        onQueueChange={handleQueueChange}
        onWindowChange={handleWindowChange}
        onWindowStatusChange={handleWindowStatusChange}
        onPreconditionExpressionChange={handlePreconditionExpressionChange}
        onScheduleSchedulingWindowChange={handleScheduleSchedulingWindowChange}
        onScheduleSchedulingWindowStatusChange={handleScheduleSchedulingWindowStatusChange}
        onScheduleStartTimeChange={handleScheduleStartTimeChange}
        onSubmitDisabledChange={handleSubmitDisabledChange}
        onSubmitAndForgetChange={handleSubmitAndForgetChange}
        onHandleStatusActionChange={handleHandleStatusActionChange}
        onWaitEventAdd={handleWaitEventAdd}
        onWaitEventRemove={handleWaitEventRemove}
        onWaitEventChange={handleWaitEventChange}
        onRaiseEventAdd={handleRaiseEventAdd}
        onRaiseEventRemove={handleRaiseEventRemove}
        onRaiseEventChange={handleRaiseEventChange}
        onLockAdd={handleLockAdd}
        onLockRemove={handleLockRemove}
        onLockChange={handleLockChange}
        onNodeIdChange={handleNodeIdChange}
        onLabelChange={handleLabelChange}
        onDescriptionChange={handleDescriptionChange}
      />
    );
  };

  const propertyPane = (
    <div key="panel" className="flow-pane mini-editor-pane" style={{ width: panelWidth }}>
      <FlowPaneHeader title={panelTitle} side="right" onCollapse={() => setPanelMode('collapsed')} onClose={handleCloseEditor} />
      <div className="flow-pane-body mini-editor">{renderPanelContent()}</div>
    </div>
  );

  // Flex row of keyed children: [library rail | library pane + handle] · canvas
  // · [handle + property pane | property rail]. Keys keep the canvas (and its
  // ReactFlow instance) mounted across collapse/expand/select, so the viewport
  // is preserved without any extra bookkeeping.
  const flowRowChildren: ReactNode[] = [];

  if (showLibrary && libraryCollapsed) {
    flowRowChildren.push(<FlowPaneRail key="library-rail" side="left" title="Node Library" onExpand={() => setLibraryCollapsed(false)} />);
  }
  if (libraryExpanded) {
    flowRowChildren.push(libraryPane);
    flowRowChildren.push(
      <Splitter
        key="library-resizer"
        size={libraryWidth}
        onResize={setLibraryWidth}
        min={FLOW_PANE_LIMITS.library.min}
        max={FLOW_PANE_LIMITS.library.max}
        side="before"
        ariaLabel="Resize node library"
      />
    );
  }

  flowRowChildren.push(canvasPane);

  if (panelOpen && panelMode === 'expanded') {
    flowRowChildren.push(
      <Splitter
        key="panel-resizer"
        size={panelWidth}
        onResize={setPanelWidth}
        min={FLOW_PANE_LIMITS.panel.min}
        max={FLOW_PANE_LIMITS.panel.max}
        side="after"
        ariaLabel="Resize property panel"
      />
    );
    flowRowChildren.push(propertyPane);
  }
  if (panelOpen && panelMode === 'collapsed') {
    flowRowChildren.push(<FlowPaneRail key="panel-rail" side="right" title={panelTitle} onExpand={() => setPanelMode('expanded')} />);
  }

  return (
    <SubflowNavigationContext.Provider value={subflowNavContextValue}>
      <div className="react-flow-container flow-split-row d-flex">{flowRowChildren}</div>
    </SubflowNavigationContext.Provider>
  );
};

export default Flow;
